import {
    faClone, faDownload, faPencilAlt,
    faRecycle,
    faStream, faTrash, faUpload, faUserCog, faUsers
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { FC } from 'react'
import { DropdownItem, DropdownMenu, DropdownToggle, FormGroup, Label, UncontrolledDropdown } from 'reactstrap'
import { isPatchInfoCurrent } from 'web/data/core-data'
import { FightCategoryIcons, GuiData } from 'web/data/gui-data'
import { Fight, FightSetup, SelectOption, SelectOptionGroup } from 'web/data/models'
import { createGroupedOptions } from 'web/utils/jsx-utils'
import { sortFights } from 'web/utils/sort-utils'
import { expectOneById, sortByDateCreated } from 'web/utils/utils'
import { FaButton } from './generic/FaButton'
import { SetupSelectBox } from './generic/SetupSelectBox'
import { Sidebar } from './shared/Sidebar'
import { CustomSelect } from './generic/CustomSelect'

type Props = {
    disableHeader: boolean

    setupData: {
        setups: FightSetup[]

        selectedSetupId?: string
        secondSelectedSetupId?: string
    }

    setupsHandlers: {
        onCreateNew: () => void
        onEdit: () => void
        onClone: () => void
        onDelete: () => void
        onSetupSelected: (setupId: string) => void
        onManageRoster: () => void
    }

    fightData: {
        fights: Fight[]
        selectedFightId: string
        legacyFights: Fight[]
    }

    fightHandlers: {
        onCreateNew: () => void
        onEdit: () => void
        onClone: () => void
        onDelete: () => void
        onPrune: () => void

        onImport: () => void
        onExport: () => void

        onFightSelected: (fightId: string) => void
    }
}

export const Header: FC<Props> = (props) => {

    const { disableHeader, fightData, fightHandlers, setupData, setupsHandlers } = props

    const noSetups = setupData.setups.length <= 0
    const canDeleteFight = fightData.fights.length >= 2

    const fightCategories = getFightCategoriesOptionGroups(fightData.fights)
    const fightOptions = createGroupedOptions({ optionGroups: fightCategories })

    const isSetupReadonly = (setupId: string | undefined) => {
        if (setupId == null) {
            return true
        }

        const setup = expectOneById(setupData.setups, setupId)
        return isPatchInfoCurrent(setup.patchInfo) === false
    }

    return (
        <div
            // Custom full-width div inside a bootstrap Container
            className={classNames(
                "d-flex flex-grow-1 align-items-center"
            )}
            style={{
                marginRight: "-15px",
                marginLeft: "-15px"
            }}
        >
            {/* Setup side */}
            <Sidebar>
                <div className="d-flex align-items-end" style={{ width: "282px" }}>
                    <FormGroup
                        className="mb-0 small mr-2"
                        // This degeneracy is needed to pull off
                        // text-wrapping inside a flexed div
                        // correctly. In short, we're saying that the
                        // minium width of this div is 0 (basis &
                        // min-width handles that), and then we tell
                        // it to grow to any remaining width.
                        //
                        // This ensures this element will cede space
                        // to any siblings, in our case the "setup"
                        // button.
                        //
                        // Similar logic is going on inside the
                        // select-box to handle the actual text
                        // wrapping.
                        style={{
                            flexBasis: 0,
                            minWidth: 0,
                            flexGrow: 1,
                        }}
                    >
                        <Label>{"Setups"}</Label>
                        <SetupSelectBox
                            input={{
                                bsSize: "sm"
                            }}
                            select={{
                                disabled: noSetups || disableHeader,
                                selectedSetup: setupData.selectedSetupId,
                                setups: sortByDateCreated(setupData.setups),
                                onSelected(option) {
                                    setupsHandlers.onSetupSelected(option.id)
                                },
                            }}
                        />

                    </FormGroup>

                    <UncontrolledDropdown>
                        <DropdownToggle
                            outline
                            size="sm"
                            disabled={disableHeader}
                            data-test={"setup-options"}
                        >
                            <span>{"Setup"}</span>
                        </DropdownToggle>

                        <DropdownMenu>
                            <DropdownItem
                                onClick={setupsHandlers.onCreateNew}
                                className="small space-mx-2"
                            >
                                <FontAwesomeIcon
                                    fixedWidth
                                    icon={faUsers}
                                    className={"text-success"}
                                />
                                <span>{`Create new setup`}</span>
                            </DropdownItem>

                            <DropdownItem divider />

                            <DropdownItem
                                disabled={noSetups || isSetupReadonly(setupData.selectedSetupId)}
                                onClick={setupsHandlers.onEdit}
                                className="small space-mx-2"
                            >
                                <FontAwesomeIcon fixedWidth icon={faPencilAlt} />
                                <span>{`Edit setup`}</span>
                            </DropdownItem>

                            <DropdownItem
                                disabled={noSetups || isSetupReadonly(setupData.selectedSetupId)}
                                onClick={setupsHandlers.onClone}
                                className="small space-mx-2"
                            >
                                <FontAwesomeIcon fixedWidth icon={faClone} />
                                <span>{`Clone setup`}</span>
                            </DropdownItem>

                            <DropdownItem
                                disabled={noSetups}
                                onClick={setupsHandlers.onDelete}
                                className={classNames("small space-mx-2")}
                            >
                                <FontAwesomeIcon
                                    fixedWidth
                                    icon={faTrash}
                                    className={classNames({ "text-danger": !noSetups })}
                                />
                                <span>{`Delete setup`}</span>
                            </DropdownItem>

                            <DropdownItem divider />

                            <DropdownItem onClick={setupsHandlers.onManageRoster} className="small space-mx-2">
                                <FontAwesomeIcon fixedWidth icon={faUserCog} />
                                <span>{"Manage roster"}</span>
                            </DropdownItem>

                        </DropdownMenu>
                    </UncontrolledDropdown>
                </div>

            </Sidebar>

            {/* Fight side */}
            <div
                style={{ width: "650px" }}
                className="pl-3 d-flex align-items-end space-mx-2"
            >
                {/*
                    TODO: This .small class needs to go because it's fucking up the size
                    on the dropdown inside of here, but if I remove it the margins get all fucked
                    compared to how they are on the setup side, I have _no_ fucking clue why
                 */}
                <FormGroup className="mb-0 flex-grow-1 small">
                    <Label>{"Fights"}</Label>

                    {/*
                        Old input, keep in case the new one is borked
                        in PROD and we need a quick revert
                     */}
                    {/* <CustomInput
                        id="select-fight"
                        type="select"
                        bsSize="sm"
                        value={fightData.selectedFightId}
                        onChange={e => fightHandlers.onFightSelected(e.target.value)}
                        disabled={disableHeader}
                    >
                        {fightOptions}
                    </CustomInput> */}

                    <CustomSelect
                        input={{
                            bsSize: 'sm',
                            disabled: disableHeader,
                        }}

                        customSelectedItemRender={(item) => {
                            const match = fightCategories.find(i => i.items.find(ii => ii.value === item.value))
                            return `${match?.label.split("—")[1].trim()} — ${item.label}`
                        }}

                        items={fightCategories}
                        selected={fightData.selectedFightId}

                        onChange={value => {

                            fightHandlers.onFightSelected(value)
                        }}
                    />

                </FormGroup>

                <UncontrolledDropdown>
                    <DropdownToggle
                        outline
                        size="sm"
                        className="flex-shrink-0"
                        disabled={disableHeader}
                        data-test={"fight-options"}
                    >
                        <span>{"Outline"}</span>
                    </DropdownToggle>

                    <DropdownMenu>
                        <DropdownItem
                            onClick={fightHandlers.onCreateNew}
                            className="small space-mx-2"
                        >
                            <FontAwesomeIcon
                                fixedWidth
                                icon={faStream}
                                className={"text-success"}
                            />
                            <span>{`Create new outline`}</span>
                        </DropdownItem>

                        <DropdownItem divider />

                        <DropdownItem
                            onClick={fightHandlers.onEdit}
                            className="small space-mx-2"
                        >
                            <FontAwesomeIcon fixedWidth icon={faPencilAlt} />
                            <span>{`Edit outline`}</span>
                        </DropdownItem>

                        <DropdownItem
                            onClick={fightHandlers.onClone}
                            className="small space-mx-2"
                        >
                            <FontAwesomeIcon fixedWidth icon={faClone} />
                            <span>{`Clone outline`}</span>
                        </DropdownItem>

                        <DropdownItem
                            disabled={!canDeleteFight}
                            onClick={fightHandlers.onDelete}
                            className={classNames("small space-mx-2")}
                        >
                            <FontAwesomeIcon
                                fixedWidth icon={faTrash}
                                className={classNames(
                                    { "text-danger": canDeleteFight }
                                )}
                            />
                            <span>{`Delete outline`}</span>
                        </DropdownItem>

                        {fightData.legacyFights.length >= 1 &&
                            <>
                                <DropdownItem divider />

                                <DropdownItem
                                    disabled={!canDeleteFight}
                                    onClick={fightHandlers.onPrune}
                                    className={classNames("small space-mx-2")}
                                >
                                    <FontAwesomeIcon fixedWidth icon={faRecycle} />
                                    <span>{`Prune historical outlines`}</span>
                                </DropdownItem>
                            </>
                        }

                    </DropdownMenu>
                </UncontrolledDropdown>

                <FaButton
                    icon={faDownload}
                    size="sm"
                    outline
                    color="primary"
                    className="flex-shrink-0"
                    disabled={disableHeader}
                    onClick={fightHandlers.onImport}
                >
                    {"Import"}
                </FaButton>

                <FaButton
                    icon={faUpload}
                    size="sm"
                    outline
                    color="primary"
                    className="flex-shrink-0"
                    disabled={disableHeader}
                    onClick={fightHandlers.onExport}
                >
                    {"Export"}
                </FaButton>
            </div>

        </div>
    )
}

function getFightCategoriesOptionGroups(fights: Fight[]): SelectOptionGroup[] {
    // DEVELOPER NOTICE - So, we loop through our fight categories and
    // create a new select-group, where we concat group and item-names
    // and make that our new group, then we add fights as items in
    // that group. This could probably be simplified further for
    // readability.
    //
    // Resulting structure
    //
    // - {group.label} - {entry.label}
    //   + {fight.name}
    //   + {fight.name}
    //   + {fight.name}

    return GuiData.FightCategoryOptions.reduce<SelectOptionGroup[]>((outerAcc, groupEntry) => {
        const innerList = groupEntry.items.reduce<SelectOptionGroup[]>((innerAcc, itemEntry) => {

            const fightOptions = fights
                .filter(fight => fight.categoryId === itemEntry.value)
                .sort(sortFights)
                .map<SelectOption>(fight => ({
                    label: fight.name,
                    value: fight.id,
                }))

            if (fightOptions.length <= 0) {
                return innerAcc
            }

            return [
                ...innerAcc,
                {
                    label: `${groupEntry.label} — ${itemEntry.label}`,
                    items: fightOptions,
                    isSelectable: true,
                    imgUrl: FightCategoryIcons[itemEntry.value]
                }
            ]

        }, [])

        if (innerList.length <= 0) {
            return outerAcc
        }

        return [
            ...outerAcc,
            ...innerList
        ]

    }, [])
}
